/**
 * LCS of two lists of strings. Returns one possible LCS.
 */
export function lcs<T extends string | number | boolean>(seq1: T[], seq2: T[]): T[] {
  const n1 = seq1.length;
  const n2 = seq2.length;
  const dp: number[][] = Array(n1 + 1)
    .fill(0)
    .map(() => Array(n2 + 1).fill(0));

  for (let i = 1; i <= n1; i++) {
    for (let j = 1; j <= n2; j++) {
      if (seq1[i - 1] === seq2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack
  const res: T[] = [];
  let i = n1;
  let j = n2;

  while (i > 0 && j > 0) {
    if (seq1[i - 1] === seq2[j - 1]) {
      res.push(seq1[i - 1]);
      i--;
      j--;
    } else {
      if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }
  }

  return res.reverse();
}

/**
 * Iterative pairwise LCS across all sequences.
 */
export function multiLCS<T extends string | number | boolean>(listOfSequences: T[][]): T[] {
  if (listOfSequences.length === 0) {
    return [];
  }

  let cur = listOfSequences[0];

  for (let i = 1; i < listOfSequences.length; i++) {
    cur = lcs(cur, listOfSequences[i]);

    if (cur.length === 0) {
      return [];
    }
  }

  return cur;
}

/**
 * Finds the longest common prefix of two arrays.
 */
export function lcp<T extends string | number | boolean>(seq1: T[], seq2: T[]): T[] {
  const minLength = Math.min(seq1.length, seq2.length);

  for (let i = 0; i < minLength; i++) {
    if (seq1[i] !== seq2[i]) {
      return seq1.slice(0, i);
    }
  }

  return seq1.slice(0, minLength);
}
